import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";

// Keeping a moving diff readable.
//
// While a turn is open the file list is rewritten every couple of seconds, and
// files arrive in path order — so a new one inserts *above* the viewport as
// often as below it, and the passage somebody is reading slides out from under
// them. Two behaviours, and the reader picks between them with the Follow
// toggle: keep the card the agent just wrote in view, or keep the card the
// reader was already looking at exactly where it was.

/** How far below the top of the scroller a followed card is parked. */
const FOLLOW_OFFSET_PX = 8;

type Anchored = { readonly offset: number; readonly path: string };

/**
 * The element that actually clips and scrolls `el`'s content. Usually `el`
 * itself, but not once a narrow viewport lets `el` grow to its content
 * height and hands scrolling to an ancestor instead (the diff pane on a
 * phone, per DiffTab.module.css's narrow-viewport rules) — walk up to
 * whichever one is really overflowing.
 */
function scrollFrameOf(el: HTMLElement): HTMLElement {
  let node: HTMLElement | null = el;
  while (node) {
    if (
      node.scrollHeight > node.clientHeight &&
      /^(auto|scroll)$/.test(getComputedStyle(node).overflowY)
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return el;
}

/**
 * @param containerRef the column of file cards.
 * @param revision     changes when the rendered diff has been rewritten.
 * @param followPath   the file to keep in view, or null to hold position.
 */
export function useDiffScrollAnchor(
  containerRef: RefObject<HTMLDivElement | null>,
  revision: number,
  followPath: string | null,
): void {
  // What the reader was looking at, sampled as they scroll rather than at the
  // moment of the rewrite: by then the DOM has already moved.
  const anchored = useRef<Anchored | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const frame = scrollFrameOf(el);
    const sample = (): void => {
      const top = frame.getBoundingClientRect().top;
      let best: Anchored | null = null;
      for (const card of el.querySelectorAll<HTMLElement>("[data-file]")) {
        const offset = card.getBoundingClientRect().top - top;
        // The first card whose top is at or above the fold — the one the
        // reader's eye is actually on.
        if (best === null || (offset <= 0 && offset > best.offset)) {
          best = { offset, path: card.dataset.file ?? "" };
        }
      }
      anchored.current = best;
    };
    sample();
    frame.addEventListener("scroll", sample, { passive: true });
    return () => {
      frame.removeEventListener("scroll", sample);
    };
  }, [containerRef]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const target = followPath ?? anchored.current?.path;
    if (!target) {
      return;
    }
    const card = el.querySelector<HTMLElement>(
      `[data-file="${CSS.escape(target)}"]`,
    );
    if (!card) {
      return;
    }
    const frame = scrollFrameOf(el);
    // Following parks the card near the top; holding position restores the
    // offset it had when the reader last looked at it, which is what makes a
    // file appearing above the viewport a non-event. Measured as a delta off
    // the frame's current position rather than an absolute offset, so it
    // holds regardless of which ancestor ends up doing the scrolling.
    const desired = followPath
      ? FOLLOW_OFFSET_PX
      : (anchored.current?.offset ?? 0);
    frame.scrollTop +=
      card.getBoundingClientRect().top -
      frame.getBoundingClientRect().top -
      desired;
    // `revision` is the whole trigger: this runs when the diff was rewritten,
    // never on an unrelated render, or it would fight the reader for the
    // scrollbar. `containerRef` is stable and `followPath` is read from the
    // render this effect was created in, so neither is a dependency.
  }, [revision]);
}

export { scrollFrameOf };
